import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { classNames } from '../../../lib/utils/classNames'
import { PropertyWithAccess } from '../../../types/Property/Property'
import { HeroIcons } from '../../../lib/utils/heroIcons'
import PropertyTabElem from './PropertyTabElem'

interface PropertyTabProps {
  classname?: string
  properties: PropertyWithAccess[]
  title: string
  emptyMessage: string
}

export default function PropertiesCard({
  classname = '',
  properties,
  title,
  emptyMessage,
}: PropertyTabProps) {
  const { t } = useTranslation('dashboard')

  const ITEMS_PER_PAGE = 5
  const [currentPage, setCurrentPage] = useState(1)

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE

  const currentProperties = properties.slice(startIndex, endIndex)

  const totalPages = Math.ceil(properties.length / ITEMS_PER_PAGE)

  useEffect(() => {
    if (totalPages === 0) {
      setCurrentPage(1)
      return
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <div
      className={classNames(
        'relative h-full overflow-hidden rounded-md bg-white p-3',
        classname,
      )}
    >
      {totalPages > 1 && (
        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-neutralGray/50 bg-neutralWhite px-2 py-0.5 shadow-2xl">
          <div className="flex items-center">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`rounded-full ${
                currentPage === 1
                  ? 'cursor-not-allowed bg-neutralGray/50 text-neutralBlack/60'
                  : 'bg-supportingBlue text-neutralWhite'
              }`}
            >
              <HeroIcons.ChevronLeftIcon className="h-4 w-4" />
            </button>

            <div className="grid grid-cols-3 gap-2 px-2">
              <div>
                {currentPage - 1 > 0 && (
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="h-5 w-5 rounded-full bg-neutralGray/50 text-xs text-neutralBlack/60"
                  >
                    {currentPage - 1}
                  </button>
                )}
              </div>
              <div>
                <button
                  onClick={() => handlePageChange(currentPage)}
                  className="h-5 w-5 cursor-not-allowed rounded-full bg-supportingBlue text-xs text-neutralWhite"
                >
                  {currentPage}
                </button>
              </div>
              <div>
                {currentPage + 1 <= totalPages && (
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="h-5 w-5 rounded-full bg-neutralGray/50 text-xs text-neutralBlack/60"
                  >
                    {currentPage + 1}
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`rounded-full ${
                currentPage === totalPages
                  ? 'cursor-not-allowed bg-neutralGray/50 text-neutralBlack/60'
                  : 'bg-supportingBlue text-neutralWhite'
              }`}
            >
              <HeroIcons.ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex h-full flex-col">
        <div className="flex pb-3">
          <div className="text-sm font-bold text-neutralBlack">{title}</div>
        </div>
        <div className="grid h-full grid-rows-6 gap-3">
          <div className="row-span-1 grid grid-cols-6 items-center border-b border-neutralGray/50 py-2 font-light text-neutralBlack/90">
            <div className="col-span-3 text-xs">
              {t('propertyTab.tab.name')}
            </div>
            <div className="text-center text-xs">
              {t('propertyTab.tab.fractionCount')}
            </div>
            <div className="text-center text-xs">
              {t('propertyTab.tab.totalValue')}
            </div>
            <div className="text-center text-xs">
              {t('propertyTab.tab.nextPayout')}
            </div>
          </div>
          {currentProperties.length > 0 ? (
            currentProperties.map((property: any) => (
              <PropertyTabElem key={property.id} property={property} />
            ))
          ) : (
            <div className="col-span-6 flex items-center justify-center py-6 text-sm text-neutralBlack/60">
              {emptyMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
